import { useRef, useEffect } from "react";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { UGGLogoFull } from "@ugitgud/legos/ui/icon/UGGLogoFull";
import { SvgUggPlusOverlay } from "@ugitgud/legos/ui/icon/SVG/UggPlusOverlay";
import { SvgUggPriceBadge } from "@ugitgud/legos/ui/icon/SVG/UggPriceBadge";
import { FeatureHighlightsBox, PLUS_FEATURE_ICONS, PLUS_FEATURE_DATA } from "@ugitgud/legos/components/plus/PlusFeatures";
import { ExclusiveFeatureCarousel } from "@ugitgud/legos/components/plus/ExclusiveFeatureCarousel";
import { ReactComponent as UggLogoU } from "@ugg/shared/assets/svg/logos/ugg-u-logo.svg";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import { usePremiumPlanPrices } from "lib/hooks/premiumHooks";

interface PlanFeature {
  name: string;
  freeUser: boolean;
  plusUser: boolean;
}

interface FeatureRowProps {
  feature: PlanFeature;
  isLast?: boolean;
}

const FeatureRow = ({ feature, isLast }: FeatureRowProps) => {
  const baseCell = classNames("py-[16px] md:py-[18px]", !isLast && "border-b border-purple-100");
  const textColor = isLast && "text-accent-yellow-400";

  return (
    <>
      <div
        className={twMerge(
          classNames(
            baseCell,
            "flex items-center text-[12px] leading-tight md:leading-none md:min-w-[247px] md:whitespace-nowrap",
            textColor,
          ),
        )}
      >
        {feature.name}
      </div>
      <div className={twMerge(classNames(baseCell, "hidden md:flex justify-center items-center text-center", textColor))}>
        {feature.freeUser && <span className="text-[24px]">✓</span>}
      </div>
      <div
        className={twMerge(
          classNames(
            baseCell,
            "relative flex justify-center items-center px-[20px] md:px-0 bg-purple-100 border-x border-[#2C2B4C]",
            textColor,
          ),
        )}
      >
        {feature.plusUser && <span className="text-[24px]">✓</span>}
        {isLast && (
          <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[#2C2B4C] via-[#F4C874] to-[#2C2B4C]" />
        )}
      </div>
      <div className={twMerge(classNames(baseCell, "hidden md:block"))} />
    </>
  );
};

export const PlusHomePage = () => {
  const { data: { pricesByDuration } = {}, loading, error } = usePremiumPlanPrices();
  const featureListRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const scrollToFeatures = () => {
    const el = document.getElementById("features");
    if (el) {
      const pageContent = document.getElementById("page-content");
      const mastheadHeight = pageContent ? parseInt(getComputedStyle(pageContent).marginTop) || 0 : 0;
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - mastheadHeight, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (!location.hash) return;
    const timer = setTimeout(scrollToFeatures, 0);
    return () => clearTimeout(timer);
  }, [location.hash]);
  const monthlyPlan = pricesByDuration?.[1];
  const annualPlan = pricesByDuration?.[12];

  const currency = monthlyPlan?.currency || "$";
  const monthlyPrice = monthlyPlan?.amount?.toFixed(2) || "3.99";
  const annualTotalPrice = annualPlan?.amount?.toFixed(2) || "29.88";
  const annualMonthlyPrice = annualPlan ? (annualPlan.amount / 12).toFixed(2) : "2.49";
  const originalMonthlyPrice = "4.99";

  // Trial info from annual plan
  const hasFreeTrial = annualPlan?.hasFreeTrial ?? false;
  const trialLength = annualPlan?.trialLength ?? 7;
  const trialUnit = annualPlan?.trialUnit ?? "days";

  const featureHighlights = [
    {
      icon: <img src={PLUS_FEATURE_ICONS.adFree} alt="Ad-Free" className="w-[116px] h-auto" />,
      title: "Ad-Free",
      description: PLUS_FEATURE_DATA.adFree.description,
    },
    {
      icon: <img src={PLUS_FEATURE_ICONS.profiles} alt="Profiles PLUS" className="w-[93px] h-auto" />,
      title: PLUS_FEATURE_DATA.profiles.title,
      description: PLUS_FEATURE_DATA.profiles.description,
    },
    {
      icon: <img src={PLUS_FEATURE_ICONS.sharedSocials} alt="Shared Socials" className="w-[144px] h-auto" />,
      title: PLUS_FEATURE_DATA.sharedSocials.title,
      description: PLUS_FEATURE_DATA.sharedSocials.description,
    },
    {
      icon: (
        <div className="relative">
          <img src={PLUS_FEATURE_ICONS.support} alt="Priority User" className="w-[119px] h-auto" />
          <UggLogoU className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[43px] h-auto" />
        </div>
      ),
      title: "Priority User",
      description: PLUS_FEATURE_DATA.support.description,
    },
  ];

  const planFeatures: PlanFeature[] = [
    { name: "Ad-Free", freeUser: false, plusUser: true },
    { name: "Personal counters & stats", freeUser: false, plusUser: true },
    { name: "Personal tier list", freeUser: false, plusUser: true },
    { name: "Profile Customization", freeUser: false, plusUser: true },
    { name: "Socials on Profile", freeUser: false, plusUser: true },
    { name: "Priority Support", freeUser: false, plusUser: true },
    { name: "Access to the Most Accurate Stats Available", freeUser: true, plusUser: true },
  ];

  return (
    <div>
      {/* Background overlay - height calculated based on feature box position */}
      <div className="absolute inset-x-0 top-0 pointer-events-none overflow-hidden z-[-1] h-[643px]">
        <div
          className="w-[200vw] -ml-[50vw] md:w-screen md:ml-0 h-auto min-h-full opacity-20 h-[643px]"
          style={{
            maskImage: "linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0) 100%)",
            WebkitMaskImage: "linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0) 100%)",
          }}
        >
          <Icon icon={<SvgUggPlusOverlay />} className="w-full h-auto min-h-full text-[#C8AC70]" />
        </div>
        <div
          className="absolute inset-0 rotate-180"
          style={{
            background: "linear-gradient(270deg, rgba(7, 7, 32, 0.80) 0%, rgba(7, 7, 32, 0.00) 50%, rgba(7, 7, 32, 0.80) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(0deg, rgba(50, 115, 250, 0.00) 0%, rgba(50, 115, 250, 0.30) 100%)",
          }}
        />
      </div>

      <div className="pt-[60px] pb-[80px] px-4 md:pt-[100px] md:pb-[120px] md:px-0">
        {/* Hero section */}
        <div className="flex flex-col items-center text-center mb-[60px] md:mb-[100px]">
          <p className="text-[18px] md:text-[24px] uppercase mb-[16px] md:mb-[24px] font-[Barlow]">
            Become <span className="font-bold">Unstoppable</span> With
          </p>
          <div className="flex items-center gap-[12px] md:gap-[24px] mb-[20px] md:mb-[28px]">
            <Icon icon={<UGGLogoFull />} className="w-[140px] md:w-[230px] h-auto" />
            <span className="rounded-[10px] md:rounded-[15px] text-[28px] md:text-[45.671px] font-bold border-[3px] md:border-[5.075px] border-accent-yellow-400 w-[100px] md:w-[162px] h-auto bg-accent-brown-400 text-accent-yellow-400">
              PLUS
            </span>
          </div>
          <p className="font-[Barlow] text-[16px] md:text-[18px] mb-[30px] md:mb-[50px] text-accent-gray-100">
            Plans starting at{" "}
            <span className="font-bold text-accent-yellow-400">
              {currency}
              {annualMonthlyPrice}*
            </span>{" "}
            per month
          </p>
          <Link
            to={`${AppRoutes.CHECKOUT}?plan=12-month`}
            className="group relative px-[12px] py-[12px] w-full max-w-[240px] rounded-[6px] font-semibold text-[18px] md:text-[20px] text-white bg-accent-brown-400 border-[1px] border-accent-yellow-400 shadow-[0_4px_2px_0_rgba(0,0,0,0.25)] transition-all text-center block"
          >
            <span
              className="absolute inset-0 rounded-[6px] opacity-0 group-hover:opacity-100 transition-opacity"
              style={{
                background: "linear-gradient(0deg, rgba(244, 200, 116, 0.05) 0%, rgba(244, 200, 116, 0.05) 100%), #25254B",
              }}
            />
            <span className="relative">
              {hasFreeTrial ? (
                <>
                  Start Your <span className="font-bold text-accent-yellow-400">Free</span> Trial
                </>
              ) : (
                "Get Started"
              )}
            </span>
          </Link>
        </div>

        {/* Exclusive feature carousel */}
        <ExclusiveFeatureCarousel
          tryFreeHref={`${AppRoutes.CHECKOUT}?plan=12-month`}
          featuresHref="#features"
          onFeaturesClick={scrollToFeatures}
        />

        {/* Feature highlights box */}
        <FeatureHighlightsBox featureHighlights={featureHighlights} className="mb-[40px] md:mb-[120px] w-full md:w-[1014px]" />

        {/* Choose a Plan section */}
        <div className="mb-[40px] md:mb-[120px]">
          <h2 className="font-[Barlow] text-[20px] md:text-[24px] font-bold uppercase text-center mb-[24px] md:mb-[36px]">
            Choose a Plan
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-[18px]">
            <div className="rounded-[8px] p-[1px] w-full max-w-[326px] bg-gradient-to-r from-[#2C2B4C] via-[#F4C874] to-[#2C2B4C] shadow-[0_2px_4px_0_rgba(0,0,0,0.25)]">
              <div
                className="relative flex flex-col items-center p-[18px] rounded-[8px] w-full"
                style={{
                  background: "linear-gradient(0deg, rgba(244, 200, 116, 0.05) 0%, rgba(244, 200, 116, 0.05) 100%), #25254B",
                }}
              >
                <div
                  className="absolute inset-x-0 top-0 mx-auto w-full max-w-[310px] h-[160px] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(50% 100% at 50% 0%, rgba(244, 200, 116, 0.10) 0%, rgba(244, 200, 116, 0.00) 100%)",
                  }}
                />
                <div className="absolute left-0 top-[76px] -translate-x-1/3 w-[80px] md:w-[100px]">
                  <Icon icon={<SvgUggPriceBadge />} className="w-full h-auto" />
                  <span className="absolute inset-0 flex flex-col items-center justify-center font-[Barlow] text-[24px] md:text-[29.517px] font-bold uppercase [text-shadow:0_1.23px_2.46px_rgba(0,0,0,0.40)] rotate-[-15.941deg]">
                    <span className="text-accent-yellow-400 -mt-2">50%</span>
                    <span className="text-[12px] md:text-[14.785px] text-white -mt-2">OFF!</span>
                  </span>
                </div>
                <h3 className="text-[28px] md:text-[32px] font-semibold mb-[11px]">Annual Plan</h3>
                <div className="font-[Barlow] text-[20px] md:text-[24px] line-through text-accent-gray-100">
                  {currency}
                  {originalMonthlyPrice}
                </div>
                <div className="font-[Barlow] flex items-baseline gap-[4px] mb-[4px] text-[40px] md:text-[48px] font-bold [text-shadow:0_2px_4px_rgba(0,0,0,0.40)] text-accent-yellow-400">
                  {currency}
                  {annualMonthlyPrice}
                </div>
                <span className="font-[Barlow] text-[20px] md:text-[24px] mb-[16px] [text-shadow:0_2px_4px_rgba(0,0,0,0.40)]">
                  /month*
                </span>
                <p className="text-[13px] md:text-[14px] text-center mb-[24px] text-accent-gray-100">
                  Recurring subscription billed annually. <br /> This can be cancelled at any time.
                </p>
                <Link
                  to={`${AppRoutes.CHECKOUT}?plan=12-month`}
                  className="group relative w-full max-w-[240px] py-[12px] px-[10px] rounded-[4px] font-bold text-[13px] mb-6 bg-accent-brown-400 border-accent-yellow-400 border-[1px] transition-all text-center block"
                >
                  <span
                    className="absolute inset-0 rounded-[4px] opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{
                      background: "linear-gradient(0deg, rgba(244, 200, 116, 0.05) 0%, rgba(244, 200, 116, 0.05) 100%), #25254B",
                    }}
                  />
                  <span className="relative">
                    {hasFreeTrial ? (
                      <>
                        Try for <span className="text-accent-yellow-400">Free</span>
                      </>
                    ) : (
                      "Get Annual Plan"
                    )}
                  </span>
                </Link>
                <p className="text-[11px] text-accent-gray-100">
                  {hasFreeTrial && (
                    <>
                      <span className="text-accent-yellow-400 bg-accent-brown-300 px-1 shadow-[0_2px_4px_0_rgba(0,0,0,0.25)]">
                        {trialLength}-{trialUnit === "days" ? "day" : trialUnit} Free Trial
                      </span>{" "}
                      Then billed at {currency}
                      {annualTotalPrice}/year
                    </>
                  )}
                  {!hasFreeTrial && (
                    <>
                      Billed at {currency}
                      {annualTotalPrice}/year
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center p-[18px] rounded-[8px] w-full max-w-[326px] bg-purple-400">
              <h3 className="text-[28px] md:text-[32px] font-semibold mb-[11px]">Monthly Plan</h3>
              <div className="font-[Barlow] text-[20px] md:text-[24px] line-through text-accent-gray-100">
                {currency}
                {originalMonthlyPrice}
              </div>
              <div className="font-[Barlow] flex items-baseline gap-[4px] mb-[4px] text-[40px] md:text-[48px] font-bold [text-shadow:0_2px_4px_rgba(0,0,0,0.40)]">
                {currency}
                {monthlyPrice}
              </div>
              <span className="font-[Barlow] text-[20px] md:text-[24px] mb-[16px] [text-shadow:0_2px_4px_rgba(0,0,0,0.40)]">
                /month*
              </span>
              <p className="text-[13px] md:text-[14px] text-center mb-[24px] text-accent-gray-100">
                Recurring subscription billed monthly. <br /> This can be cancelled at any time.
              </p>
              <Link
                to={`${AppRoutes.CHECKOUT}?plan=1-month`}
                className="w-full max-w-[240px] py-[12px] px-[10px] rounded-[4px] font-bold text-[13px] mb-6 bg-lavender-500 text-center block hover:bg-lavender-400 transition-colors"
              >
                Get Monthly Plan
              </Link>
              <p className="text-[11px] text-accent-gray-100">*20% off regular price</p>
            </div>
          </div>
        </div>

        {/* Feature List section */}
        <div id="features" ref={featureListRef} className="scroll-mt-[var(--masthead-height)]">
          <h2 className="font-Barlow text-[20px] md:text-[24px] font-bold uppercase text-center mb-[24px] md:mb-[36px]">
            Feature List
          </h2>
          <div className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_153px_153px_1fr] rounded-[8px] mt-6 px-4 md:pl-6 bg-purple-400 w-full max-w-[668px] mx-auto border border-[#2C2B4C]">
            {/* Table Header */}
            <div className="text-[12px] md:text-[14px] font-bold pl-2 md:pl-3 border-l-[2px] rounded-[2px] border-l-accent-blue-400 self-center">
              Features
            </div>
            <div className="hidden md:flex text-[14px] font-medium text-center px-[44px] self-center">Free User</div>
            <div className="relative flex flex-col items-center text-center pt-4 md:pt-6 px-[20px] md:px-[44px] pb-[10px] md:pb-[12px] bg-purple-100 border-x border-[#2C2B4C]">
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[#2C2B4C] via-[#F4C874] to-[#2C2B4C]" />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "radial-gradient(50% 100% at 50% 0%, rgba(244, 200, 116, 0.10) 0%, rgba(244, 200, 116, 0.00) 100%)",
                }}
              />
              {hasFreeTrial && (
                <span className="absolute -top-2 text-[10px] md:text-[11px] text-accent-yellow-400 font-semi-bold bg-accent-brown-300 px-[4px] md:px-[6px] py-[2px] shadow-[0_2px_4px_0_rgba(0,0,0,0.40)]">
                  {trialLength}-{trialUnit === "days" ? "day" : trialUnit} Free Trial
                </span>
              )}
              <Icon icon={<UGGLogoFull />} className="w-[45px] md:w-[57.49px] h-auto mb-1" />
              <span className="px-[4px] md:px-[6px] py-[2px] rounded-[2px] text-[10px] md:text-[11.418px] font-semibold border-[1.27px] bg-accent-brown-300 border-accent-yellow-400 text-accent-yellow-400 rounded-[3.806px]">
                PLUS
              </span>
            </div>
            <div className="hidden md:block" />
            <div className="col-span-2 md:col-span-4 h-[1px] bg-purple-500 -mx-4 md:-mx-6 w-[calc(100%+32px)] md:w-[calc(100%+48px)]" />
            {/* Table Body */}
            {planFeatures.map((feature, index) => (
              <FeatureRow key={index} feature={feature} isLast={index === planFeatures.length - 1} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
